import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';

import { isDefined } from '../../../../../utils/variables';
import { GeodataSetupContext } from '../../../../state/geodata-setup-state';
import { Stack } from '../../../stack/stack';
import { GcpFileImporter } from '../../gcp-file-importer/gcp-file-importer';
import { GcpRow } from '../../gcp-parser';

const UploadGcp = () => {
  const context = useContext(GeodataSetupContext);

  // Only pass initialData from context when we have existing data, and memoize so we
  // don't overwrite the importer's selected file on every re-render (parent's selectedFile
  // is only set after import, so passing it would clear the user's choice before Import).
  const initialData = useMemo(() => {
    if (!isDefined(context) || context.gcpPoints.length === 0) {
      return undefined;
    }
    return {
      filename: context.gcpFilename ?? '',
      gcpPoints: context.gcpPoints,
    };
  }, [context]);

  const handleImport = React.useCallback(
    (data: { filename: string; gcpPoints: GcpRow[] }) => {
      if (isDefined(context)) {
        context.setGcp({
          filename: data.filename,
          gcpPoints: data.gcpPoints,
        });
      }
    },
    [context],
  );

  const handleDataChange = React.useCallback(
    (data: { filename: string; gcpPoints: GcpRow[] }) => {
      if (isDefined(context)) {
        if (data.gcpPoints.length === 0) {
          context.setGcp({
            filename: '',
            gcpPoints: [],
          });
        } else {
          context.setGcp({
            filename: data.filename,
            gcpPoints: data.gcpPoints,
          });
        }
      }
    },
    [context],
  );

  return (
    <Container>
      <Content>
        <PointsContainer>
          <GcpFileImporter
            initialData={initialData}
            onDataChange={handleDataChange}
            onImport={handleImport}
          />
        </PointsContainer>
      </Content>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const PointsContainer = styled(Stack)`
  flex: 1;
  overflow: hidden;
  min-height: 0;
  width: 100%;

  /* Prevent horizontal scrolling */
  > * {
    max-width: 100%;
    overflow-x: hidden;
  }
`;

export { UploadGcp };
